import { motion } from "framer-motion";
import { CheckCircle2, MapPin } from "lucide-react";
import { BRAND } from "@/lib/brand";

const credentials = [
  "QPFP — Qualified Personal Finance Professional",
  "AMFI Registered Mutual Fund Distributor",
  "IRDAI Licensed Insurance Agent — Life & General",
  "NISM V-A: Mutual Fund Distributors Certification",
  "NISM X-A: Investment Adviser Level 1",
];

export default function AboutParimal() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Photo column */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] rounded-[28px] overflow-hidden">
            <img
              src={BRAND.ownerPhoto}
              alt="Parimal Mistry, Founder of Akshar Investments"
              className="w-full h-full object-cover"
              data-testid="about-owner-photo"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0B1B3D]/40 via-transparent to-transparent" />
          </div>

          {/* Signature plaque */}
          <div className="absolute -bottom-6 -right-4 md:-right-10 bg-white border border-black/5 rounded-2xl px-6 py-4 shadow-2xl max-w-[260px]">
            <div className="overline text-[10px] text-[#00A65A]">Founder</div>
            <div className="font-display text-2xl text-[#0B1B3D] leading-none mt-1">
              Parimal Mistry
            </div>
            <div className="text-xs text-[#0B1B3D]/60 mt-1.5 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Navsari, Gujarat
            </div>
          </div>

          {/* Decorative number */}
          <div className="hidden md:flex absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[#00A65A] text-white flex-col items-center justify-center shadow-xl">
            <span className="big-number text-3xl leading-none text-white">18+</span>
            <span className="overline text-[8px] mt-1 !text-white">Years</span>
          </div>
        </motion.div>

        {/* Copy column */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <span className="inline-flex items-center gap-2 text-sm font-medium text-[#00A65A]">
            <span className="w-6 h-px bg-[#00A65A]" />
            About the founder
          </span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#0B1B3D] leading-[1.04]">
            Two decades of <span className="italic font-light text-[#00A65A]">conviction</span>,
            built one family at a time.
          </h2>

          <p className="mt-6 text-base md:text-lg text-[#0B1B3D]/75 leading-relaxed max-w-2xl">
            A seasoned Personal Finance Professional with over <strong>18 years</strong> of expertise
            in the asset management industry, Parimal specialises in mutual fund advisory, wealth
            creation strategies, and investor education. Nearly two decades inside a dynamic AMC
            environment have shaped his deep understanding of market cycles, investor behaviour,
            and SEBI/IRDAI regulatory frameworks.
          </p>

          <p className="mt-5 text-base md:text-lg text-[#0B1B3D]/75 leading-relaxed max-w-2xl">
            His approach is unapologetically client-centric — aligning every product to a real life
            goal, never the other way around.
          </p>

          <div className="mt-9 grid sm:grid-cols-2 gap-3">
            {credentials.map((c) => (
              <div
                key={c}
                data-testid={`credential-${c.split(' ')[0].toLowerCase()}`}
                className="flex items-start gap-3 text-sm text-[#0B1B3D]/80"
              >
                <CheckCircle2 className="w-5 h-5 text-[#00A65A] flex-shrink-0 mt-0.5" />
                <span>{c}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
